import { useEffect, useState } from "react";
import { AuthProvider, useAuth } from "./components/AuthContext.tsx";
import { DialogProvider, useDialog } from "./components/DialogManager.tsx";
import { ToastProvider, useToast } from "./components/ToastContext.tsx";
import { ErrorBoundary } from "./components/ErrorBoundary.tsx";
import { CookieNotice } from "./components/widgets/CookieNotice.tsx";
import { ToSNotice } from "./components/widgets/ToSNotice.tsx";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import ForgotPasswordPage from "./pages/ForgotPasswordPage.tsx";
import { LoginPage } from "./pages/LoginPage.tsx";
import { ProfilePage } from "./pages/ProfilePage.tsx";
import { PrivacyPage } from "./pages/PrivacyPage.tsx";
import TermsPage from "./pages/TermsPage.tsx";
import ContactPage from "./pages/ContactPage.tsx";
import { Header } from "./components/Header.tsx";
import { Footer } from "./components/Footer.tsx";
import { ProtectedRoute, PublicRoute } from "./components/AuthRoutes.tsx";
import { HomePage } from "./pages/HomePage.tsx";
import { setQueryErrorHandler } from "./api/queryClient.ts";
import { User } from "./api/types.ts";
import { useFriendRequests, useFriends, useProfile } from "./api/hooks.ts";
import { SocialPage } from "./pages/SocialPage.tsx";
import { isNotNullish } from "./util/typeGuards.ts";
import { UnderMaintenance } from "./components/UnderMaintenance.tsx";

const UNDER_MAINTENANCE = false;

function ProtectedPages({
  profile,
  profileLoading,
  profileError,
  friends,
}: {
  profile?: User;
  profileLoading?: boolean;
  profileError: Error | null;
  friends: Partial<User>[] | undefined;
}) {
  const { openDialog, currentDialog } = useDialog();
  const [profileEdited, setProfileEdited] = useState(false);

  // Check if profile is incomplete and show dialog (only once)
  useEffect(() => {
    if (UNDER_MAINTENANCE) {
      return;
    }

    const missingProfileData =
      profile &&
      (!profile.purpose ||
        !profile.preferred_resistance_unit ||
        !profile.preferred_time_format ||
        !profile.preferred_visibility);

    if (
      !profileEdited &&
      profile &&
      missingProfileData &&
      !(currentDialog === "profile-settings" || currentDialog === "onboarding")
    ) {
      if (localStorage.getItem("onboarding-completed") !== "true") {
        openDialog("onboarding", {});
      }
      openDialog("profile-settings");
      setProfileEdited(true);
    }
  }, [currentDialog, openDialog, profile, profileEdited]);

  if (UNDER_MAINTENANCE) {
    return (
      <UnderMaintenance
        title="Stay Tuned!"
        text="seriv.io is currently under maintenance. We'll be back very soon."
      />
    );
  }

  return (
    <HomePage
      profile={profile}
      profileLoading={profileLoading}
      profileError={profileError}
      friends={friends}
    />
  );
}

function AppContent() {
  const { showError } = useToast();

  const {
    data: friendRequests,
    error: friendRequestsError,
    isFetching: friendRequestsLoading,
  } = useFriendRequests();

  const {
    data: friends,
    error: friendsError,
    isFetching: friendsLoading,
  } = useFriends();

  useEffect(() => {
    // Set up the global error handler for React Query
    setQueryErrorHandler(showError);
  }, [showError]);

  const { user } = useAuth();

  const {
    data: profile,
    isLoading: profileLoading,
    error: profileError,
  } = useProfile(!!user);

  return (
    <ErrorBoundary
      onError={(error) => {
        showError(error.message || "An unexpected error occurred");
      }}
    >
      <DialogProvider profile={profile}>
        <BrowserRouter>
          <div className="App flex flex-col min-h-screen">
            <Header
              hasNotifications={
                isNotNullish(friendRequests) && friendRequests.length > 0
              }
            />
            <main className="flex-grow">
              <Routes>
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <ProtectedPages
                        profile={profile}
                        profileLoading={profileLoading}
                        profileError={profileError}
                        friends={friends}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workouts"
                  element={
                    <ProtectedRoute>
                      <ProtectedPages
                        profile={profile}
                        profileLoading={profileLoading}
                        profileError={profileError}
                        friends={friends}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <ProtectedPages
                        profile={profile}
                        profileLoading={profileLoading}
                        profileError={profileError}
                        friends={friends}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/routines"
                  element={
                    <ProtectedRoute>
                      <ProtectedPages
                        profile={profile}
                        profileLoading={profileLoading}
                        profileError={profileError}
                        friends={friends}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/exercises"
                  element={
                    <ProtectedRoute>
                      <ProtectedPages
                        profile={profile}
                        profileLoading={profileLoading}
                        profileError={profileError}
                        friends={friends}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/activity"
                  element={
                    <ProtectedRoute>
                      <ProtectedPages
                        profile={profile}
                        profileLoading={profileLoading}
                        profileError={profileError}
                        friends={friends}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage
                        profile={profile}
                        profileLoading={profileLoading}
                        profileError={profileError}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/social"
                  element={
                    <ProtectedRoute>
                      <SocialPage
                        profile={profile}
                        profileLoading={profileLoading}
                        profileError={profileError}
                        friendRequests={friendRequests}
                        friendRequestsError={friendRequestsError}
                        friendRequestsLoading={friendRequestsLoading}
                        friends={friends}
                        friendsError={friendsError}
                        friendsLoading={friendsLoading}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/login"
                  element={
                    <PublicRoute>
                      <LoginPage />
                    </PublicRoute>
                  }
                />
                <Route
                  path="/forgot"
                  element={
                    <PublicRoute>
                      <ForgotPasswordPage />
                    </PublicRoute>
                  }
                />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/contact" element={<ContactPage />} />
              </Routes>
            </main>
            <Footer />
            <CookieNotice />
            <ToSNotice />
          </div>
        </BrowserRouter>
      </DialogProvider>
    </ErrorBoundary>
  );
}

function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ToastProvider>
  );
}

export default App;
